// UserMenu — капсула «[время] [ФИО] [аватар]» справа сверху с меню пользователя
// (Профиль / Задачи / Лента / Выход). Часы тикают в TZ школы.
import { LogOut, ListTodo, Bell, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/lib/auth-context';
import { SCHOOL_TZ } from '@/lib/format';

import { useMyProfile } from '../hooks/useMyProfile';

import { MyProfileModal } from './MyProfileModal';

const TIME_FMT = new Intl.DateTimeFormat('ru-RU', {
  timeZone: SCHOOL_TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function useClock(): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  return TIME_FMT.format(now);
}

export function UserMenu(): JSX.Element {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const profile = useMyProfile();
  const time = useClock();

  const [open, setOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Показываем ФИО из профиля (GET /me/profile); фолбэк — user.fullName (сейчас
  // это email, т.к. /auth/me не отдаёт ФИО).
  const displayName =
    profile.data && (profile.data.last_name || profile.data.first_name)
      ? [profile.data.last_name, profile.data.first_name].filter(Boolean).join(' ')
      : (user?.fullName ?? '');
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
  const photoUrl = profile.data?.photo_url;

  function go(path: string): void {
    setOpen(false);
    navigate(path);
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-full bg-whisper-white pl-3 pr-1 text-sm font-medium text-ink-black hover:bg-divider-gray/40"
        aria-label="Меню пользователя"
        title={displayName}
      >
        {/* Часы на телефоне ни к чему: они есть в статус-баре системы, а
            место в шапке дороже (план 110). */}
        <span className="tabular-nums text-muted-text max-lg:hidden">{time}</span>
        {displayName ? (
          <span className="hidden max-w-[11rem] truncate md:inline">{displayName}</span>
        ) : null}
        <span className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-lavender-mist text-xs font-semibold text-ink-black">
          {photoUrl && !imgFailed ? (
            <img
              src={photoUrl}
              alt=""
              onError={() => setImgFailed(true)}
              className="h-full w-full object-cover"
            />
          ) : (
            initials || <User className="h-4 w-4" />
          )}
        </span>
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-52 overflow-hidden rounded-card border border-divider-gray bg-canvas-white shadow-lg">
          <div className="border-b border-divider-gray px-3 py-2">
            <div className="truncate text-sm font-medium text-ink-black">{displayName}</div>
            {profile.data ? (
              <div className="truncate text-xs text-muted-text">{user?.fullName}</div>
            ) : null}
          </div>
          <MenuItem
            icon={<User className="h-4 w-4" />}
            label="Профиль"
            onClick={() => {
              setOpen(false);
              setShowProfile(true);
            }}
          />
          <MenuItem
            icon={<ListTodo className="h-4 w-4" />}
            label="Задачи"
            onClick={() => go('/tasks')}
          />
          <MenuItem
            icon={<Bell className="h-4 w-4" />}
            label="Лента"
            onClick={() => go('/notifications')}
          />
          <button
            type="button"
            onClick={() => void signOut().then(() => navigate('/login', { replace: true }))}
            className="flex w-full items-center gap-2 border-t border-divider-gray px-3 py-2 text-left text-sm text-destructive hover:bg-muted"
          >
            <LogOut className="h-4 w-4" />
            Выход
          </button>
        </div>
      ) : null}

      {/* Окно — в body, а не внутри шапки: шапка sticky с z-20 — свой контекст
          наложения, и на телефоне нижняя навигация (z-30) перекрывала низ
          профиля — кнопку «Сохранить» и панель QR привязки Telegram. */}
      {showProfile
        ? createPortal(<MyProfileModal onClose={() => setShowProfile(false)} />, document.body)
        : null}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
    >
      {icon}
      {label}
    </button>
  );
}
